
import { useCallback, useEffect, useRef, useState } from 'react';

export interface AudioPreview {
  playingKey: string | null;
  positionMs: number;
  durationMs: number;
  toggle: (key: string, src: string) => void;
  seek: (ms: number) => void;
  stop: () => void;
}

export function useAudioPreview(): AudioPreview {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const [positionMs, setPositionMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const playingRef = useRef<string | null>(null);

  const setPlaying = useCallback((key: string | null) => {
    playingRef.current = key;
    setPlayingKey(key);
  }, []);

  const ensureElement = useCallback(() => {
    if (!audioRef.current) {
      const element = new Audio();
      element.preload = 'none';
      element.addEventListener('ended', () => setPlaying(null));
      element.addEventListener('error', () => setPlaying(null));
      element.addEventListener('timeupdate', () =>
        setPositionMs(Math.round(element.currentTime * 1000)),
      );
      element.addEventListener('loadedmetadata', () =>
        setDurationMs(Number.isFinite(element.duration) ? Math.round(element.duration * 1000) : 0),
      );
      audioRef.current = element;
    }
    return audioRef.current;
  }, [setPlaying]);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    setPlaying(null);
    setPositionMs(0);
  }, [setPlaying]);

  const seek = useCallback((ms: number) => {
    const element = audioRef.current;
    if (!element || !Number.isFinite(element.duration)) return;
    const target = Math.max(0, Math.min(element.duration * 1000, ms));
    element.currentTime = target / 1000;
    setPositionMs(Math.round(target));
  }, []);

  const toggle = useCallback(
    (key: string, src: string) => {
      const element = ensureElement();
      if (playingRef.current === key) {
        element.pause();
        setPlaying(null);
        return;
      }
      element.pause();
      if (element.src !== src) {
        element.src = src;
        setPositionMs(0);
        setDurationMs(0);
      }
      setPlaying(key);
      void element.play().catch(() => setPlaying(null));
    },
    [ensureElement, setPlaying],
  );

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  return { playingKey, positionMs, durationMs, toggle, seek, stop };
}
